import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Calendar as CalendarPicker } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { useApi } from "../../../hooks/useApi";
import { toast } from "react-toastify";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { ArrowLeft, CalendarDays, Gauge, Loader2, Route, TrendingUp, Activity, FileSpreadsheet, FileDown } from "lucide-react";
import { exportStatsToPdf, exportStatsToXls } from "../../../utils/exportStatsReport";
import { cn } from "../../../lib/utils";
import { useIsMobile } from "../../../hooks/use-breakpoint";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const RANGE_TODAY = "today";
const RANGE_PAST_7_DAYS = "past7";
const RANGE_CUSTOM = "custom";
const CUSTOM_STEP_FROM = "from";
const CUSTOM_STEP_TO = "to";

/** Display dates as mm-dd-yyyy */
function formatMMDDYYYY(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${yyyy}`;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function safeNumber(v, fallback = 0) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parseTripTime(trip) {
  const value =
    trip?.trip_start_time ||
    trip?.startTime ||
    trip?.start_time ||
    trip?.created_at ||
    trip?.createdAt;
  if (!value) return null;
  const raw = String(value).trim();
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);
  if (!Number.isNaN(d.getTime())) return d;

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatXAxisLabel(d, mode) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "N/A";
  if (mode === RANGE_TODAY) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return formatMMDDYYYY(d);
}

function extractDistanceKm(trip) {
  const candidates = [
    trip?.distanceKm,
    trip?.trip_distance_km,
    trip?.trip_distance,
    trip?.distance_km,
    trip?.distance,
    trip?.distanceInKm,
    trip?.total_distance_km,
  ];
  for (const v of candidates) {
    const n = safeNumber(v, NaN);
    if (Number.isFinite(n)) return n > 1000 ? n / 1000 : n;
  }
  return 0;
}

function extractAvgSpeedKmph(trip) {
  const candidates = [
    trip?.avg_speed,
    trip?.average_speed,
    trip?.speed,
    trip?.max_speed,
    trip?.top_speed,
  ];
  for (const v of candidates) {
    const n = safeNumber(v, NaN);
    if (Number.isFinite(n)) return n;
  }
  const distanceKm = extractDistanceKm(trip);
  const durationMinutes = safeNumber(trip?.durationMinutes ?? trip?.duration_minutes, 0);
  if (distanceKm > 0 && durationMinutes > 0) {
    return (distanceKm / durationMinutes) * 60;
  }
  return 0;
}

export default function VehicleStatsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId: deviceIdParam } = useParams();
  const { get } = useApi();

  const deviceId = deviceIdParam ? decodeURIComponent(deviceIdParam) : "";
  const mapBasePath = location.pathname.startsWith("/admin") ? "/admin" : "/customer";
  const isMobile = useIsMobile();
  const vehicleLabel =
    location.state?.vehicle_number ||
    location.state?.vehicle_name ||
    (deviceId ? `${deviceId.slice(0, 8)}…` : "Vehicle");
  const maxSelectableDate = useMemo(() => startOfDay(new Date()), []);
  const [rangeMode, setRangeMode] = useState(RANGE_TODAY);
  const [customFrom, setCustomFrom] = useState(startOfDay(new Date()));
  const [customTo, setCustomTo] = useState(endOfDay(new Date()));
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [customDateStep, setCustomDateStep] = useState(CUSTOM_STEP_FROM);

  const dateBounds = useMemo(() => {
    const now = new Date();
    if (rangeMode === RANGE_TODAY) {
      return { from: startOfDay(now), to: endOfDay(now) };
    }
    if (rangeMode === RANGE_PAST_7_DAYS) {
      const to = endOfDay(now);
      const from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
      return { from, to };
    }
    const from = startOfDay(customFrom || now);
    const to = endOfDay(customTo || customFrom || now);
    return from <= to ? { from, to } : { from: startOfDay(to), to: endOfDay(from) };
  }, [rangeMode, customFrom, customTo]);

  useEffect(() => {
    const loadTrips = async () => {
      if (!deviceId) {
        setTrips([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await get(
          `/trips?device_id=${encodeURIComponent(deviceId)}&page=1&per_page=500&sortBy=trip_start_time&sortOrder=desc`
        );
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.trips)
              ? response.trips
              : response?.data && Array.isArray(response.data.data)
                ? response.data.data
                : [];
        setTrips(list);
      } catch (err) {
        toast.error(err?.message || "Failed to load stats data");
        setTrips([]);
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [deviceId, get]);

  const filteredTrips = useMemo(() => {
    return trips
      .map((trip) => ({ ...trip, __startDate: parseTripTime(trip) }))
      .filter((trip) => trip.__startDate && trip.__startDate >= dateBounds.from && trip.__startDate <= dateBounds.to)
      .sort((a, b) => a.__startDate - b.__startDate);
  }, [trips, dateBounds.from, dateBounds.to]);

  const metrics = useMemo(() => {
    const labels = filteredTrips.map((trip) => formatXAxisLabel(trip.__startDate, rangeMode));
    const distances = filteredTrips.map((trip) => Number(extractDistanceKm(trip).toFixed(2)));
    const speeds = filteredTrips.map((trip) => Number(extractAvgSpeedKmph(trip).toFixed(1)));
    const totalDistance = Number(distances.reduce((sum, v) => sum + v, 0).toFixed(2));
    const avgSpeed = speeds.length > 0 ? Number((speeds.reduce((sum, v) => sum + v, 0) / speeds.length).toFixed(1)) : 0;
    return { labels, distances, speeds, totalDistance, avgSpeed };
  }, [filteredTrips, rangeMode]);

  const distanceData = useMemo(
    () => ({
      labels: metrics.labels,
      datasets: [
        {
          label: "Distance (km)",
          data: metrics.distances,
          borderColor: "rgb(99, 102, 241)",
          backgroundColor: "rgba(99, 102, 241, 0.22)",
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    }),
    [metrics]
  );

  const speedData = useMemo(
    () => ({
      labels: metrics.labels,
      datasets: [
        {
          label: "Avg Speed (km/h)",
          data: metrics.speeds,
          backgroundColor: "rgba(16, 185, 129, 0.8)",
          borderColor: "rgb(5, 150, 105)",
          borderWidth: 1,
          borderRadius: 8,
          maxBarThickness: 42,
        },
      ],
    }),
    [metrics]
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(6, 12, 22, 0.95)",
          titleFont: { size: isMobile ? 11 : 13 },
          bodyFont: { size: isMobile ? 10 : 12 },
          borderColor: "rgba(16, 185, 129, 0.35)",
          borderWidth: 1,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(34, 197, 94, 0.12)" },
          ticks: {
            font: { size: isMobile ? 10 : 12 },
            callback: (value) => `${value} km`,
          },
          border: { color: "rgba(16, 185, 129, 0.18)" },
        },
        x: {
          grid: { color: "rgba(16, 185, 129, 0.06)" },
          border: { color: "rgba(16, 185, 129, 0.18)" },
          ticks: {
            font: { size: isMobile ? 9 : 11 },
            maxRotation: isMobile ? 45 : 0,
            autoSkip: true,
            maxTicksLimit: isMobile ? 6 : 12,
          },
        },
      },
    }),
    [isMobile]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(6, 12, 22, 0.95)",
          titleFont: { size: isMobile ? 11 : 13 },
          bodyFont: { size: isMobile ? 10 : 12 },
          borderColor: "rgba(52, 211, 153, 0.35)",
          borderWidth: 1,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(34, 197, 94, 0.12)" },
          ticks: {
            font: { size: isMobile ? 10 : 12 },
            callback: (value) => `${value} km/h`,
          },
          border: { color: "rgba(16, 185, 129, 0.18)" },
        },
        x: {
          grid: { color: "rgba(16, 185, 129, 0.06)" },
          border: { color: "rgba(16, 185, 129, 0.18)" },
          ticks: {
            font: { size: isMobile ? 9 : 11 },
            maxRotation: isMobile ? 45 : 0,
            autoSkip: true,
            maxTicksLimit: isMobile ? 6 : 12,
          },
        },
      },
    }),
    [isMobile]
  );

  const sameCustomDay =
    dateBounds.from instanceof Date &&
    dateBounds.to instanceof Date &&
    !Number.isNaN(dateBounds.from.getTime()) &&
    !Number.isNaN(dateBounds.to.getTime()) &&
    dateBounds.from.toDateString() === dateBounds.to.toDateString();

  const rangeLabel = sameCustomDay
    ? formatMMDDYYYY(dateBounds.from)
    : `${formatMMDDYYYY(dateBounds.from)} to ${formatMMDDYYYY(dateBounds.to)}`;
  const avgTripDistance = filteredTrips.length
    ? Number((metrics.totalDistance / filteredTrips.length).toFixed(2))
    : 0;
  const totalDriveMinutes = filteredTrips.reduce(
    (sum, trip) => sum + safeNumber(trip?.durationMinutes ?? trip?.duration_minutes, 0),
    0
  );
  const totalDriveHours = Number((totalDriveMinutes / 60).toFixed(1));

  const exportDisabled = loading || filteredTrips.length === 0;

  const handleExportXls = () => {
    exportStatsToXls({
      deviceId,
      rangeLabel,
      filteredTrips,
      metrics,
      avgTripDistance,
      totalDriveHours,
    });
  };

  const handleExportPdf = () => {
    exportStatsToPdf({
      deviceId,
      rangeLabel,
      filteredTrips,
      metrics,
      avgTripDistance,
      totalDriveHours,
    });
  };

  const rangeModeButtons = [
    { id: RANGE_TODAY, label: isMobile ? "Today" : "Today" },
    { id: RANGE_PAST_7_DAYS, label: isMobile ? "7 Days" : "Past 7 Days" },
    { id: RANGE_CUSTOM, label: isMobile ? "Custom" : "Custom Date" },
  ];

  return (
    <div
      className={cn(
        "w-full min-w-0 bg-background text-foreground",
        "pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:pb-6"
      )}
    >
      {calendarOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 backdrop-blur-[6px] bg-black/10"
        />
      )}

      {/* Mobile top bar — scrolls with content (no sticky; avoids fighting app Header) */}
      <div className="flex items-center gap-2 pb-2 sm:hidden">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-lg"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Statistics
          </p>
          <h1 className="truncate text-sm font-semibold text-foreground">{vehicleLabel}</h1>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl space-y-2.5 sm:space-y-5 sm:p-3 md:p-6">
        {/* Desktop header */}
        <div className="hidden sm:block rounded-2xl border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818] p-4 md:p-5 shadow-[0_10px_40px_rgba(5,150,105,0.12)] md:rounded-3xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-emerald-700 dark:text-emerald-300/80">
                Vehicle Analytics
              </p>
              <h1 className="mt-1 text-xl md:text-3xl font-semibold tracking-tight text-foreground">
                Statistics Dashboard
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">{vehicleLabel} · Trip insights</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="h-9 text-sm border-1 border-muted-foreground/20"
                disabled={exportDisabled}
                onClick={handleExportXls}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                XLS
              </Button>
              <Button
                variant="outline"
                className="h-9 text-sm border-1 border-muted-foreground/20"
                disabled={exportDisabled}
                onClick={handleExportPdf}
              >
                <FileDown className="mr-2 h-4 w-4" />
                PDF
              </Button>
              <Button variant="outline" className="h-9 text-sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </div>
        </div>

        {/* Date range — segmented control */}
        <div className="rounded-xl border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818] p-2.5 sm:rounded-2xl sm:p-4">
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted/50 p-1">
            {rangeModeButtons.map((btn) => (
              <button
                key={btn.id}
                type="button"
                onClick={() => {
                  setRangeMode(btn.id);
                  if (btn.id === RANGE_CUSTOM) setCustomDateStep(CUSTOM_STEP_FROM);
                }}
                className={cn(
                  "h-9 rounded-lg text-xs font-semibold transition-all sm:h-10 sm:text-sm",
                  rangeMode === btn.id
                    ? "bg-background text-foreground shadow-sm ring-1 ring-emerald-500/40"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {btn.label}
              </button>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              <CalendarDays className="h-3.5 w-3.5" />
              {rangeLabel}
            </p>
            {!loading && (
              <p className="text-[11px] text-muted-foreground">
                {filteredTrips.length} trip{filteredTrips.length === 1 ? "" : "s"}
              </p>
            )}
          </div>

          {rangeMode === RANGE_CUSTOM && (
            <div className="mt-3">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10 w-full justify-start border-1 border-muted-foreground/20 bg-background/60 text-sm"
                  >
                    <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
                    Pick dates
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  className="z-50 w-[min(20rem,92vw)] border-1 border-muted-foreground/20 bg-background p-2"
                >
                  <div className="rounded-lg border-1 border-muted-foreground/20 bg-muted/20 p-2">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-medium text-muted-foreground">
                        {customDateStep === CUSTOM_STEP_FROM ? "From date" : "To date"}
                      </p>
                      {customDateStep === CUSTOM_STEP_TO && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => setCustomDateStep(CUSTOM_STEP_FROM)}
                        >
                          Back
                        </Button>
                      )}
                    </div>
                    <CalendarPicker
                      mode="single"
                      selected={customDateStep === CUSTOM_STEP_FROM ? customFrom : customTo}
                      disabled={(date) => date > maxSelectableDate}
                      className="w-full p-0"
                      onSelect={(d) => {
                        if (!d) return;
                        if (customDateStep === CUSTOM_STEP_FROM) {
                          setCustomFrom(d);
                          if (!customTo || d > customTo) setCustomTo(d);
                          setCustomDateStep(CUSTOM_STEP_TO);
                          return;
                        }
                        setCustomTo(d);
                        setCalendarOpen(false);
                      }}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Compact stat tiles — mobile */}
        <div className="grid grid-cols-2 gap-2 sm:hidden">
          {[
            {
              label: "Trips",
              value: filteredTrips.length,
              hint: "In range",
              icon: Activity,
              accent: "text-emerald-500",
            },
            {
              label: "Avg speed",
              value: `${metrics.avgSpeed}`,
              unit: "km/h",
              hint: `${totalDriveHours}h drive`,
              icon: Gauge,
              accent: "text-sky-500",
            },
            {
              label: "Distance",
              value: metrics.totalDistance,
              unit: "km",
              hint: `Avg ${avgTripDistance} km`,
              icon: Route,
              accent: "text-indigo-500",
            },
            {
              label: "Avg trip",
              value: avgTripDistance,
              unit: "km",
              hint: "Per trip",
              icon: TrendingUp,
              accent: "text-amber-500",
            },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="rounded-xl border-1 border-muted-foreground/20 bg-muted/10 px-3 py-2.5 dark:bg-[#181818]"
              >
                <div className="flex items-center justify-between gap-1">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {stat.label}
                  </p>
                  <Icon className={cn("h-3.5 w-3.5 shrink-0", stat.accent)} />
                </div>
                <p className="mt-1 text-xl font-semibold tabular-nums leading-none text-foreground">
                  {stat.value}
                  {stat.unit ? (
                    <span className="ml-0.5 text-xs font-normal text-muted-foreground">{stat.unit}</span>
                  ) : null}
                </p>
                <p className="mt-1 text-[10px] text-muted-foreground">{stat.hint}</p>
              </div>
            );
          })}
        </div>

        {/* Stat cards — desktop / tablet grid */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[
            { label: "Trips", value: filteredTrips.length, sub: "Active records", icon: Activity },
            { label: "Total Distance", value: `${metrics.totalDistance} km`, sub: `Avg ${avgTripDistance} km/trip`, icon: TrendingUp },
            { label: "Average Speed", value: `${metrics.avgSpeed} km/h`, sub: `${totalDriveHours} h driving`, icon: Gauge },
            { label: "Avg Trip", value: `${avgTripDistance} km`, sub: "Per trip average", icon: Route },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.label}
                className="border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818]"
              >
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm">{stat.label}</CardDescription>
                  <CardTitle className="text-2xl md:text-3xl">{stat.value}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon className="h-4 w-4" />
                    {stat.sub}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts */}
        {loading ? (
          <Card className="border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818]">
            <CardContent className="flex h-56 sm:h-72 items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                Loading analytics…
              </div>
            </CardContent>
          </Card>
        ) : filteredTrips.length === 0 ? (
          <Card className="border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818]">
            <CardContent className="flex h-56 flex-col items-center justify-center gap-2 px-4 text-center sm:h-72">
              <Route className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium">No trips in this range</p>
              <p className="text-xs text-muted-foreground">Try Today, 7 Days, or pick another date.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 sm:grid sm:grid-cols-1 sm:gap-6 xl:grid-cols-2">
            <Card className="overflow-hidden border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818]">
              <CardHeader className="border-b border-muted-foreground/10 bg-gradient-to-r from-indigo-500/15 to-transparent pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Route className="h-5 w-5 text-indigo-500" />
                  Distance
                </CardTitle>
                <CardDescription className="text-xs">Trip-wise trend</CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-3 pt-3 sm:px-6 sm:pb-6">
                <div className="h-[200px] sm:h-[280px] md:h-[320px]">
                  <Line data={distanceData} options={lineOptions} />
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-1 border-muted-foreground/20 bg-muted/10 dark:bg-[#181818]">
              <CardHeader className="border-b border-muted-foreground/10 bg-gradient-to-r from-emerald-500/15 to-transparent pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Gauge className="h-5 w-5 text-emerald-500" />
                  Speed
                </CardTitle>
                <CardDescription className="text-xs">Average km/h per trip</CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-3 pt-3 sm:px-6 sm:pb-6">
                <div className="h-[200px] sm:h-[280px] md:h-[320px]">
                  <Bar data={speedData} options={barOptions} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Mobile export bar — above home indicator */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-muted-foreground/15 bg-background/95 backdrop-blur-md sm:hidden safe-bottom">
        <div className="grid grid-cols-2 gap-2 px-3 py-3 safe-x">
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-xl border-1 border-emerald-500/30 bg-emerald-500/5 font-semibold"
            disabled={exportDisabled}
            onClick={handleExportXls}
          >
            <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" />
            Export XLS
          </Button>
          <Button
            type="button"
            className="h-11 rounded-xl border-1 border-muted-foreground/20 bg-muted/30 font-semibold text-foreground hover:bg-muted/50"
            disabled={exportDisabled}
            onClick={handleExportPdf}
          >
            <FileDown className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>
    </div>
  );
}
